import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';

import { getPolygonStyle } from './styles';
import {
    FERTILITY_RATE_COLORS, fertilityRateLegendURL, fertilityRateLegendURLNepali,
    CATEGORIZED_CHANGE_COLORS, categorizedChangeLegendURL, categorizedChangeLegendURLNepali,
    CHANGE_AMT_COLORS, changeAmtLegendURL, changeAmtLegendURLNepali, slantLinePatternColor
} from './choroplethLegends';
import { sharedPolygonSources, makeDivisionWiseLayerGroup } from './helpers';
import { findDataForCode } from '../api';


export const centroidLayer = new VectorLayer({
    source: new VectorSource(),
    zIndex: 1000,
});

const styles = {
    state: getPolygonStyle("rgba(0,0,0,0.1)", "rbga(255,255,255,0)", 1.5, 1),
    district: getPolygonStyle("rgba(0,0,0,0.1)", "rbga(255,255,255,0)", 1, 1),
    municipality: getPolygonStyle("rgba(0,0,0,0)", "rbga(255,255,255,0)", 0.2, 1),
};

export const fertilityLayers = makeDivisionWiseLayerGroup({
    sources: sharedPolygonSources,
    styles: styles,
    zIndex: 0.1,
    opacity: 0.8,
});

export const setFertilityOpacity = opacity =>
{
    Object.values(fertilityLayers).forEach(layer => layer.setOpacity(parseFloat(opacity)) );
};

export const updateFertilityStyle = ({stateData, districtData, municipalityData, colorMap}) =>
{
    fertilityLayers.state.setStyle(feature =>
    {
        const dataItem = findDataForCode(stateData, feature.get('SCode'))
        const color = dataItem ? colorMap(dataItem.value) : slantLinePatternColor;
        styles.state.getFill().setColor(color);
        return styles.state;
    });
    fertilityLayers.district.setStyle(feature =>
    {
        const dataItem = findDataForCode(districtData, feature.get('DCode'))
        const color = dataItem ? colorMap(dataItem.value) : slantLinePatternColor;
        styles.district.getFill().setColor(color);
        return styles.district;
    });
    fertilityLayers.municipality.setStyle(feature =>
    {
        const dataItem = findDataForCode(municipalityData, feature.get('SDDMM'))
        const color = dataItem ? colorMap(dataItem.value) : slantLinePatternColor;
        styles.municipality.getFill().setColor(color);
        return styles.municipality;
    });
};

export const setFertilityLayersVisible = (visible) =>
{
    Object.values(fertilityLayers).forEach(layer => layer.setVisible(visible));
};

export const fertilityLayerGroupInfo = [
    {
        id: 'oafr',
        title: 'Overall adolescent fertility',
        titleNepali: 'समग्र किशोरकिशोरी प्रजनन्',
        layers: [
            {
                id: 'afr2011',
                field: 'afr2011',
                visible: false,
                legendIsShown: false,
                colorMap: value => FERTILITY_RATE_COLORS[value],
                titleNepali: 'समग्र किशोरकिशोरी प्रजनन्  दर, २०११',
                titleEnglish: "Overall AFR, 2011",
                legendPath: fertilityRateLegendURL.dataurl,
                legendPathNepali: fertilityRateLegendURLNepali.dataurl
            },
            {
                id: 'afr2016',
                field: 'afr2016',
                colorMap: value => FERTILITY_RATE_COLORS[value],
                legendIsShown: false,
                visible: false,
                titleNepali: 'समग्र किशोरकिशोरी प्रजनन्  दर, २०१६',
                titleEnglish: "Overall AFR, 2016",
                legendPath: fertilityRateLegendURL.dataurl,
                legendPathNepali: fertilityRateLegendURLNepali.dataurl
            },
            {
                id: 'oafr',
                field: 'oafr',
                colorMap: value => CATEGORIZED_CHANGE_COLORS[value],
                legendIsShown: true,
                visible: true,
                titleNepali: "समग्र किशोरकिशोरी प्रजनन् दर बर्गिकरण, २०१६",
                titleEnglish: "Overall fertility category, 2011-2016",
                legendPath: categorizedChangeLegendURL.dataurl,
                legendPathNepali: categorizedChangeLegendURLNepali.dataurl
            },
            {
                id: 'afrchange',
                field: 'afrchange',
                colorMap: value => CHANGE_AMT_COLORS[value],
                legendIsShown: false,
                visible: false,
                titleNepali: "किशोरकिशोरी प्रजनन् दरमा वृद्धि (%), २०११-२०१६",
                titleEnglish: "Change in overall AFR (%), 2011-2016",
                legendPath: changeAmtLegendURL.dataurl,
                legendPathNepali: changeAmtLegendURLNepali.dataurl
            },
        ]
    },
    {
        id: 'mafr',
        title: 'Marital adolescent fertility',
        titleNepali: 'वैवाहिक किशोरकिशोरी प्रजनन्',
        layers: [
            {
                id: 'mafr2011',
                field: 'mafr2011',
                legendIsShown: false,
                visible: false,
                colorMap: value => FERTILITY_RATE_COLORS[value],
                titleNepali: 'वैवाहिक किशोरकिशोरी प्रजनन् दर, २०११',
                titleEnglish: "Marital AFR, 2011",
                legendPath: fertilityRateLegendURL.dataurl,
                legendPathNepali: fertilityRateLegendURLNepali.dataurl
            },
            {
                id: 'mafr2016',
                field: 'mafr2016',
                colorMap: value => FERTILITY_RATE_COLORS[value],
                legendIsShown: false,
                visible: false,
                titleNepali: 'वैवाहिक किशोरकिशोरी प्रजनन् दर, २०१६',
                titleEnglish: "Marital AFR, 2016",
                legendPath: fertilityRateLegendURL.dataurl,
                legendPathNepali: fertilityRateLegendURLNepali.dataurl
            },
            {
                id: 'omafr',
                field: 'omafr',
                colorMap: value => CATEGORIZED_CHANGE_COLORS[value],
                legendIsShown: false,
                visible: false,
                titleNepali: "वैवाहिक किशोरकिशोरी प्रजनन् दर बर्गिकरण, २०१६",
                titleEnglish: "Overall marital fertility category, 2011-2016",
                legendPath: categorizedChangeLegendURL.dataurl,
                legendPathNepali: categorizedChangeLegendURLNepali.dataurl

            },
            {
                id: 'mafrchange',
                field: 'mafrchange',
                colorMap: value => CHANGE_AMT_COLORS[value],
                legendIsShown: false,
                visible: false,
                titleNepali: "वैवाहिक किशोरकिशोरी प्रजनन् दरमा वृद्धि (%), २०११-२०१६",
                titleEnglish: "Change in marital AFR (%), 2011-2016",
                legendPath: changeAmtLegendURL.dataurl,
                legendPathNepali: changeAmtLegendURLNepali.dataurl
            },
        ]
    },
];

